// Minicurso do Parceiro — o conteúdo do Vigor Academy.
//
// Os vídeos e PDFs ficam no bucket privado `academy` do Storage (lidos por URL
// assinada) e são gerados fora do app: os vídeos pela skill /video
// (output/videos/2026-10-26-academy-parceiro/), os PDFs a partir da mesma regra.
// Mudou a regra de bonificação (src/lib/bonificacaoParceiro.ts)? O simulador
// acompanha sozinho; os vídeos 6 e 7 e o PDF da regra precisam ser refeitos.
// Os ids 'comissao' e 'tabela-comissao' ficaram com o nome antigo de propósito:
// são chaves estáveis.

export const BUCKET_ACADEMY = 'academy';

export interface Aula {
    /** Chave estável: é o que fica gravado em academy_progresso.aula_id. */
    id: string;
    numero: number;
    titulo: string;
    descricao: string;
    /** Duração do vídeo, como aparece na lista (m:ss). */
    duracao: string;
    video: string;
    capa: string;
    /** O que a aula ensina, em texto — para quem não pode ouvir ou quer revisar. */
    pontos: string[];
}

export interface Modulo {
    id: string;
    titulo: string;
    aulas: Aula[];
}

export interface Material {
    id: string;
    titulo: string;
    descricao: string;
    arquivo: string;
    /** Nome do arquivo baixado. */
    nomeDownload: string;
}

const aula = (numero: number, a: Omit<Aula, 'numero' | 'video' | 'capa'>): Aula => ({
    ...a,
    numero,
    video: `parceiro/aula-${numero}.mp4`,
    capa: `parceiro/aula-${numero}.jpg`,
});

export const MODULOS_PARCEIRO: Modulo[] = [
    {
        id: 'comece',
        titulo: 'Comece por aqui',
        aulas: [
            aula(1, {
                id: 'parceiro-1-primeiro-acesso',
                titulo: 'Primeiro acesso',
                descricao: 'Onde fica cada coisa no VigorHub: o painel, o CRM, as configurações e os avisos.',
                duracao: '1:19',
                pontos: [
                    'Entre em hub.vigorenergy.com.br com o e-mail e a senha que a Vigor criou para você.',
                    'No painel, Ações Rápidas cria uma negociação, cria uma tarefa ou abre a agenda.',
                    'Os seus números do mês ficam logo abaixo; troque o período no canto de cima.',
                    'No alto: Dashboard é o painel, CRM são os seus clientes e a engrenagem abre as configurações.',
                    'O sino mostra os avisos do sistema. Número vermelho em cima dele é novidade esperando.',
                ],
            }),
            aula(2, {
                id: 'parceiro-2-cadastro',
                titulo: 'Seu cadastro e sua conta',
                descricao: 'Perfil, a conta bancária onde a bonificação cai e o cadastro da sua equipe.',
                duracao: '1:27',
                pontos: [
                    'Em Conta e Perfil, confira o nome, o e-mail pessoal e o CPF. Em Segurança, troque a senha.',
                    'Enquanto faltar a conta bancária, o CRM mostra um aviso amarelo.',
                    'Em Dados Bancários, clique em Adicionar Conta e preencha banco, tipo, agência, conta e titular.',
                    'A bonificação cai sempre na conta marcada como principal.',
                    'Vendedores que trabalham com você: Usuários e Permissões › Adicionar Usuário, como Vendedor Parceiro.',
                    'A venda da sua equipe também soma na sua faixa do mês.',
                ],
            }),
        ],
    },
    {
        id: 'crm',
        titulo: 'Vendendo pelo CRM',
        aulas: [
            aula(3, {
                id: 'parceiro-3-funil',
                titulo: 'O funil de vendas',
                descricao: 'As cinco etapas da venda, o que o cartão do cliente mostra e o seu resultado do mês.',
                duracao: '1:32',
                pontos: [
                    'Prospecção, Em contato, Qualificação, Em negociação e Termo de adesão: as cinco etapas.',
                    'Arraste o cartão para mudar o cliente de etapa, ou troque a etapa dentro da ficha.',
                    '"Esfriando" quer dizer muitos dias sem interação: hora de retomar o contato.',
                    'No cartão ficam o consumo em kWh e os atalhos: criar visita, ligar e WhatsApp.',
                    'A faixa Meu Trabalho mostra os termos em assinatura, os assinados (em validação, reprovados e aprovados) e a bonificação garantida, o Pix I do mês.',
                ],
            }),
            aula(4, {
                id: 'parceiro-4-cliente-novo',
                titulo: 'Cadastrando um cliente',
                descricao: 'O botão Nova Negociação, no automático (pela conta de luz) ou no manual.',
                duracao: '1:37',
                pontos: [
                    'Todo cliente novo entra pelo botão Nova Negociação, no canto de baixo do CRM.',
                    'Automático: escolha pessoa física ou jurídica, envie o documento e a conta de luz em PDF.',
                    'Cada conta de luz enviada vira uma unidade consumidora do cliente.',
                    'Manualmente: tipo de cliente, nome, WhatsApp, CPF, e-mail e quem paga o boleto.',
                    'Unidade consumidora e documentos podem entrar agora ou depois, pela ficha.',
                    'Confira o consumo médio em kWh: é ele que entra na faixa do mês e na sua bonificação.',
                ],
            }),
            aula(5, {
                id: 'parceiro-5-plano-termo',
                titulo: 'Plano, simulação e termo',
                descricao: 'Da proposta à assinatura: plano, simulação, termo de adesão e a análise da Vigor.',
                duracao: '1:38',
                pontos: [
                    'Clique no nome do cliente para abrir a ficha.',
                    'O plano tem desconto na tarifa, desconto na bandeira, carência e fidelidade (padrão de 12 meses).',
                    'O desconto padrão é 20%. Mais que isso é condição especial e precisa da aprovação da Vigor.',
                    'Carência precisa da aprovação do gerente antes de valer.',
                    'Nova simulação gera a proposta com a economia do cliente.',
                    'Novo termo manda o termo de adesão por e-mail para o cliente assinar.',
                    'Se o e-mail não chegar, em Ver termos use Copiar link e mande pelo WhatsApp.',
                    'Depois da assinatura, a Vigor analisa o cadastro e liga para o consumidor. Com o termo aprovado, a venda conta para a sua faixa do mês.',
                ],
            }),
        ],
    },
    {
        id: 'comissao',
        titulo: 'Sua bonificação',
        aulas: [
            aula(6, {
                id: 'parceiro-6-calculo',
                titulo: 'Como calcular a bonificação',
                descricao: 'A faixa do mês, a conta em três passos e quanto o desconto tira.',
                duracao: '3:05',
                pontos: [
                    'A faixa vem da soma do consumo dos consumidores que a Vigor aceitou no mês, e vale para todos eles.',
                    'Faixas: até 2.500 kWh 20%, até 5.000 25%, até 10.000 45%, até 20.000 60%, até 39.999 80%, de 40.000 para cima 120%.',
                    'Fatura com desconto = consumo médio × R$ 1,16 × (1 − desconto).',
                    'Bonificação = fatura com desconto × % da faixa × redutor.',
                    'O plano padrão é 20% de desconto e não tem redutor. De 21% a 25%, cada ponto tira 2 pontos; de 26% a 35%, tira 3. Acima de 20% precisa da aprovação da Vigor.',
                    'Desconto máximo pelo tamanho: até 1.499 kWh 25%, até 4.999 30%, de 5.000 para cima 35%. Mínimo de 500 kWh por consumidor.',
                    'Exemplo: 6.000 kWh com 28% → fatura de R$ 5.011,20 × 45% = R$ 2.255,04 × redutor de 81% = R$ 1.826,58.',
                ],
            }),
            aula(7, {
                id: 'parceiro-7-recebimento',
                titulo: 'Quando você recebe',
                descricao: 'Pix I e Pix II, Fixa ou Variável, como a Vigor paga e o que muda o valor.',
                duracao: '2:15',
                pontos: [
                    'Pix I, 30% da bonificação: dia 10 do mês seguinte ao aceite do consumidor.',
                    'Fixa: Pix II, 70%, no dia 10 do mês seguinte à 1ª fatura paga pelo consumidor.',
                    'Variável: o mesmo Pix I e, depois, 2% × redutor de cada fatura paga, por 3 anos depois da assinatura.',
                    'A Vigor paga uma vez por mês, somando todos os seus consumidores, com nota fiscal da sua empresa. Abaixo de R$ 50, passa para o mês seguinte.',
                    'Fatura não paga não gera pagamento. 1ª fatura sem pagamento em 60 dias: o Pix I sai das próximas bonificações.',
                    'Em Configurações › Meu Dinheiro: o que já recebeu e o histórico. O pagamento vai para a conta principal em Dados Bancários.',
                ],
            }),
        ],
    },
];

export const AULAS_PARCEIRO: Aula[] = MODULOS_PARCEIRO.flatMap(m => m.aulas);

export const MATERIAIS_PARCEIRO: Material[] = [
    {
        id: 'guia-rapido',
        titulo: 'Guia rápido do parceiro',
        descricao: 'O caminho de uma venda no VigorHub, do cadastro à bonificação.',
        arquivo: 'parceiro/guia-rapido-do-parceiro.pdf',
        nomeDownload: 'Guia rápido do parceiro - Vigor Energy.pdf',
    },
    {
        id: 'tabela-comissao',
        titulo: 'Regra de bonificação',
        descricao: 'As faixas do mês, a conta, o redutor do desconto e quando cada Pix é pago.',
        arquivo: 'parceiro/regra-de-bonificacao.pdf',
        nomeDownload: 'Regra de bonificação do parceiro - Vigor Energy.pdf',
    },
];
